/**
 * Enhanced Chat Real Estate Service - Consultative Version
 * 
 * KOPS-343: Enhanced on January 2, 2026
 * 
 * Changes from chat-realestate-conversational.service.ts:
 * - Added consultative conversation flow that asks about lifestyle/motivation before searching
 * - Implemented conversation stages: greeting → discovery → qualifying → confirming → searching
 * - Added conversation depth tracking (minimum 3 exchanges before showing properties)
 * - Enhanced context extraction to include motivation, timeline, constraints, and lifestyle
 * - Added suggested actions UI support for guided conversation
 * - Requires comprehensive context before allowing property searches
 * - Generates lifestyle-based property match reasons
 * 
 * Goal: Make the chatbot feel like a real estate consultant who understands 
 * the "why" behind the move, not just a property search tool.
 */

import axiosInstance from '../helpers/api';

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface PropertyResult {
  id: string;
  mlsNumber: string;
  address: string;
  city?: string;
  state?: string;
  zipCode?: string;
  price: number;
  beds: number;
  baths: number;
  sqft: number;
  images?: string[];
  latitude?: number;
  longitude?: number;
  whyItMatches?: string;
  listingBrokerage?: string;
  listingAgent?: string;
  description?: string;
  // Rich MLS data
  yearBuilt?: number;
  daysOnMarket?: number;
  propertyType?: string;
  features?: string[];
  hoaFee?: number;
  lotSize?: number;
  garage?: number;
  pool?: boolean;
  pricePerSqft?: number;
  architecturalStyle?: string;
  viewType?: string;
  communityAmenities?: string[];
  subdivision?: string;
  schoolDistrict?: string;
  elementarySchool?: string;
  virtualTour?: string;
  mlsArea?: string;
  fireplace?: boolean;
  listingDate?: string;
  status?: string;
}

// Rich property data for AI analysis (no images to save memory)
interface PropertyForAI {
  id: string;
  mlsNumber: string;
  address: string;
  city: string;
  price: number;
  beds: number;
  baths: number;
  sqft: number;
  yearBuilt?: number;
  daysOnMarket?: number;
  propertyType?: string;
  features?: string[];
  description?: string;
  hoaFee?: number;
  lotSize?: number;
  garage?: number;
  pool?: boolean;
  pricePerSqft?: number;
  taxAssessedValue?: number;
  schoolRatings?: {
    elementary?: number;
    middle?: number;
    high?: number;
  };
  recentRenovations?: string[];
  architecturalStyle?: string;
  viewType?: string; // mountain, city, golf course, etc.
  masterPlanned?: boolean;
  communityAmenities?: string[];
  fireplace?: boolean;
  // Enhanced fields for comprehensive AI responses
  subdivision?: string;
  schoolDistrict?: string;
  priceReduction?: boolean;
  listingDate?: string;
  modifiedDate?: string;
  virtualTour?: string;
  spa?: boolean;
  conditionInsights?: any;
  mlsArea?: string;
  status?: string;
  allFeatures?: string[];
}

interface ChatResponse {
  message: string;
  properties?: PropertyResult[];
  mentionedProperties?: PropertyResult[];
  intent?: {
    type: string;
    parameters: any;
  };
  suggestedActions?: Array<{
    label: string;
    action: string;
  }>;
  shouldSearchProperties?: boolean;
  hasEnoughContext?: boolean;
  searchProgress?: {
    matchCount: number;
    searchQuality: 'initial' | 'too_broad' | 'refining' | 'optimal' | 'ready' | 'no_results';
  };
  searchAction?: {
    trigger: boolean;
    criteria: any;
    query: string;
    type: 'new_search' | 'refine_search';
  };
  appointmentAction?: {
    trigger: boolean;
    type: string;
    agentInfo: any;
    message: string;
  };
  searchExecuted?: boolean;
  searchCriteria?: any;
}

interface ConversationContext {
  hasLocation: boolean;
  hasBudget: boolean;
  hasFeatures: boolean;
  hasMotivation: boolean;
  hasTimeline: boolean;
  hasConstraints: boolean;
  conversationStage: 'greeting' | 'discovery' | 'qualifying' | 'confirming' | 'searching' | 'viewing';
  conversationDepth: number;
  extractedCriteria: {
    city?: string;
    state?: string;
    zipCode?: string;
    maxPrice?: number;
    minPrice?: number;
    beds?: number;
    baths?: number;
    bedrooms?: number;
    bathrooms?: number;
    propertyType?: string;
    features?: string[];
    purpose?: string;
    motivation?: string;
    timeline?: string;
    constraints?: string[];
    lifestyle?: string[];
    yearBuilt?: number;
    minYearBuilt?: number;
    maxYearBuilt?: number;
    minSqft?: number;
    maxSqft?: number;
  };
}

class ChatRealEstateConsultativeService {
  protected sessionId: string;
  private conversationHistory: ChatMessage[] = [];
  private lastSearchedProperties: PropertyResult[] = []; // Store last search results
  private lastSearchedPropertiesForAI: PropertyForAI[] = []; // Lightweight version for AI context
  protected context: ConversationContext = {
    hasLocation: false,
    hasBudget: false,
    hasFeatures: false,
    hasMotivation: false,
    hasTimeline: false,
    hasConstraints: false,
    conversationStage: 'greeting',
    conversationDepth: 0,
    extractedCriteria: {}
  };
  
  // Public getter for sessionId
  public getSessionId(): string {
    return this.sessionId;
  }

  constructor() {
    this.sessionId = this.generateSessionId();
    this.initializeConversation();
    console.log('🚀 [CONSULTATIVE] Service initialized with sessionId:', this.sessionId);
  }

  private generateSessionId(): string {
    return `chat_${Date.now()}_${Math.random().toString(36).substring(7)}`;
  }

  /**
   * Extract features from deal breakers text
   */
  private extractFeaturesFromDealBreakers(dealBreakers: string[]): string[] {
    const features: string[] = [];
    const featureKeywords = {
      'pool': ['pool', 'swimming pool'],
      'modern': ['modern', 'contemporary', 'updated'],
      'good schools': ['school', 'schools', 'school district'],
      'garage': ['garage', 'parking'],
      'yard': ['yard', 'lawn', 'backyard'],
      'kitchen': ['kitchen', 'granite', 'stainless'],
      'master': ['master', 'primary bedroom', 'ensuite'],
      'fireplace': ['fireplace'],
      'view': ['view', 'mountain view', 'city view']
    };
    
    // Join all deal breakers into one text
    const text = dealBreakers.join(' ').toLowerCase();
    
    // Extract matching features
    Object.entries(featureKeywords).forEach(([feature, keywords]) => {
      if (keywords.some(keyword => text.includes(keyword))) {
        features.push(feature);
      }
    });
    
    return features;
  }

  private initializeConversation() {
    // Check if we're on aisearchportal.com
    const isAISearchPortal = window.location.hostname.includes('aisearchportal.com');
    
    // Check for buyer context from portal
    const buyerContextStr = localStorage.getItem('portalBuyerContext');
    const sanitizedContextStr = localStorage.getItem('portalBuyerContextSanitized');
    let buyerContext: any = null;
    let sanitizedBuyerContext: any = null;
    let systemPrompt = `You are a consultative real estate advisor on chat.realestate who helps people make informed home-buying decisions. Your approach:

1. ALWAYS start by understanding WHY they're moving before WHAT they want
2. Ask about their lifestyle, daily routines, and what's driving their move
3. Explore their timeline and any constraints they face
4. Only after understanding their full situation should you search for properties
5. Be conversational, empathetic, and genuinely interested in their story
6. Don't rush to show properties - build trust and understanding first
7. Ask open-ended questions that reveal motivations and priorities
8. When they give basic criteria, dig deeper into the "why" behind it

Remember: You're not just finding houses, you're helping people find homes that fit their life journey.`;
    
    if (buyerContextStr && isAISearchPortal) {
      try {
        buyerContext = JSON.parse(buyerContextStr);
        // Use sanitized context for LLM if available
        sanitizedBuyerContext = sanitizedContextStr ? JSON.parse(sanitizedContextStr) : buyerContext;
        console.log('🏠 [CONSULTATIVE] AISearchPortal - Initializing with buyer context:', buyerContext);
        
        // Update context with buyer preferences
        if (buyerContext.preferences) {
          const prefs = buyerContext.preferences;
          
          // Update extracted criteria
          if (prefs.budgetMin || prefs.budgetMax) {
            this.context.hasBudget = true;
            this.context.extractedCriteria.minPrice = prefs.budgetMin;
            this.context.extractedCriteria.maxPrice = prefs.budgetMax;
          }
          
          if (prefs.cities && prefs.cities.length > 0) {
            this.context.hasLocation = true;
            this.context.extractedCriteria.city = prefs.cities.join(', ');
          }
          
          if (prefs.bedrooms) {
            this.context.hasFeatures = true;
            this.context.extractedCriteria.beds = prefs.bedrooms;
            this.context.extractedCriteria.bedrooms = prefs.bedrooms;
          }
          
          if (prefs.bathrooms) {
            this.context.extractedCriteria.baths = prefs.bathrooms;
            this.context.extractedCriteria.bathrooms = prefs.bathrooms;
          }
          
          if (prefs.propertyType) {
            this.context.extractedCriteria.propertyType = prefs.propertyType;
          }
          
          // Add deal breakers and lifestyle preferences
          if (prefs.dealBreakers && prefs.dealBreakers.length > 0) {
            this.context.hasFeatures = true;
            this.context.extractedCriteria.lifestyle = prefs.dealBreakers;
            this.context.extractedCriteria.features = this.extractFeaturesFromDealBreakers(prefs.dealBreakers);
          }
          
          if (prefs.motivation) {
            this.context.hasMotivation = true;
            this.context.extractedCriteria.motivation = prefs.motivation;
          }
          
          if (prefs.timeline) {
            this.context.hasTimeline = true;
            this.context.extractedCriteria.timeline = prefs.timeline;
          }
          
          // For AISearchPortal, use a direct search approach with sanitized context
          const contextForPrompt = sanitizedBuyerContext || buyerContext;
          const agentInfo = contextForPrompt.agentInfo || {};
          const agentName = agentInfo.name || 'Your agent';
          const agentBrokerage = agentInfo.brokerage || agentInfo.brokerageName || '';
          
          // Store search history if available
          if (contextForPrompt.searchHistory) {
            (this as any).agentSearchHistory = contextForPrompt.searchHistory;
          }
          
          systemPrompt = `You are an AI real estate search assistant working with ${agentName}${agentBrokerage ? ` from ${agentBrokerage}` : ''}.
${agentName} has invited ${contextForPrompt.firstName || 'the buyer'} to use this personalized property search portal.

Agent Information:
- Name: ${agentName}
- Brokerage: ${agentBrokerage || 'Contact agent for brokerage details'}
- Email: ${agentInfo.email || 'Not provided'}
- Phone: ${agentInfo.phone || 'Not provided'}

Buyer Information:
- Name: ${contextForPrompt.firstName || 'Buyer'}
- Budget: $${prefs.budgetMin?.toLocaleString() || '0'} - $${prefs.budgetMax?.toLocaleString() || 'any'}
- Looking in: ${prefs.cities?.join(', ') || 'Not specified'}
- Property type: ${prefs.propertyType || 'Any'}
- Bedrooms: ${prefs.bedrooms || 'Any'}
- Bathrooms: ${prefs.bathrooms || 'Any'}
${prefs.squareFeetMin ? `- Min Square Feet: ${prefs.squareFeetMin}` : ''}
${prefs.motivation ? `- Motivation: ${prefs.motivation}` : ''}
${prefs.timeline ? `- Timeline: ${prefs.timeline}` : ''}
${prefs.preApproved ? `- Pre-approved: Yes ($${prefs.preApprovalAmount})` : ''}
${prefs.dealBreakers && prefs.dealBreakers.length > 0 ? `\nDeal Breakers / Must-Haves:\n${prefs.dealBreakers.map(db => `- ${db}`).join('\n')}` : ''}

${contextForPrompt.searchHistory && contextForPrompt.searchHistory.length > 0 ? `\nRecent Searches by ${agentName}:
${contextForPrompt.searchHistory.slice(0, 5).map(s => `- "${s.query}" (${s.resultsCount} properties found)`).join('\n')}` : ''}

${contextForPrompt.hasRecommendations ? `\n${agentName} has already saved some properties they think you'll love!` : ''}

${contextForPrompt.conversationContext?.hasInteracted ? `\nNote: You last spoke with ${contextForPrompt.firstName} on ${new Date(contextForPrompt.conversationContext.lastInteractionDate).toLocaleDateString()}` : ''}

When responding:
1. Always acknowledge that you're working with ${agentName} as their trusted real estate professional
2. Reference that ${agentName} has already discussed their needs and shared their preferences
3. Focus on finding properties that match their specific requirements
4. Maintain a professional yet friendly tone that reinforces the agent-buyer relationship
5. ${contextForPrompt.searchHistory && contextForPrompt.searchHistory.length > 0 ? `Mention that ${agentName} has already performed searches and ask if they'd like to see those results` : ''}
6. ${contextForPrompt.conversationContext?.hasInteracted ? 'Reference previous conversations naturally' : 'This is their first visit'}`;
          
          // Set conversation stage to searching since we have preferences
          this.context.conversationStage = 'searching';
          this.context.conversationDepth = 5; // Skip minimum conversation requirements
          
          // Mark that we should auto-search on first interaction
          (this as any).shouldAutoSearch = true;
        }
      } catch (error) {
        console.error('Failed to parse buyer context:', error);
      }
    } else if (!isAISearchPortal) {
      console.log('💬 [CONSULTATIVE] Chat.realestate - Starting consultative flow');
    }
    
    // Enhanced system prompt for consultative conversation
    this.conversationHistory.push({
      role: 'system',
      content: systemPrompt
    });
  }

  /**
   * Main entry point for chat interactions
   */
  public async sendMessage(userMessage: string): Promise<ChatResponse> {
    // Track conversation depth
    this.context.conversationDepth++;
    
    // Add user message to history
    this.conversationHistory.push({
      role: 'user',
      content: userMessage
    });
    
    // Always include property context if we have properties and user is asking about them
    const isPropertyQuestion = this.isAskingAboutProperties(userMessage);
    console.log('🔍 [CONSULTATIVE] Property question check:', {
      userMessage,
      isPropertyQuestion,
      propertiesAvailable: this.lastSearchedPropertiesForAI.length,
      willAddContext: this.lastSearchedPropertiesForAI.length > 0 && isPropertyQuestion
    });
    
    if (this.lastSearchedPropertiesForAI.length > 0 && isPropertyQuestion) {
      // Remove any previous property context to avoid duplication
      this.conversationHistory = this.conversationHistory.filter(msg => 
        !msg.content.includes('[ASSISTANT CONTEXT: The user is asking about')
      );
      
      // Since backend doesn't use conversation history properly, 
      // we need to inject property data into the user message itself
      const propertyData = this.formatPropertiesForAIContext();
      
      // Modify the last user message to include property context
      this.conversationHistory[this.conversationHistory.length - 1].content = 
        `${userMessage}\n\n[Context: User is viewing these properties:\n${propertyData}\n\nFor "largest" questions, identify the property marked [LARGEST].]`;
      
      // Add property context right before the user's message
      const propertyContext = `You have access to ${this.lastSearchedPropertiesForAI.length} properties the user is currently viewing. Use this data to answer their questions:\n\n${propertyData}`;
      const lastUserMessageIndex = this.conversationHistory.length - 1;
      this.conversationHistory.splice(lastUserMessageIndex, 0, {
        role: 'system',
        content: propertyContext
      });
      
      console.log('🏠 [CONSULTATIVE] Added property context for:', userMessage);
      console.log('🏠 [CONSULTATIVE] Property context preview:', propertyContext.substring(0, 200) + '...');
      console.log('🏠 [CONSULTATIVE] Conversation history length:', this.conversationHistory.length);
      console.log('🏠 [CONSULTATIVE] Properties included:', this.lastSearchedPropertiesForAI.map(p => ({
        address: p.address,
        sqft: p.sqft,
        price: p.price
      })));
    } else {
      console.log('🏠 [CONSULTATIVE] No property context added:', {
        hasProperties: this.lastSearchedPropertiesForAI.length,
        isAskingAboutProperties: this.isAskingAboutProperties(userMessage),
        userMessage
      });
    }

    // Extract context from message
    this.updateContext(userMessage);

    // Determine if we should be searching yet
    const hasEnoughContext = this.hasComprehensiveContext();
    
    // Run background search to get match count (don't wait for results)
    let searchProgress = await this.getSearchProgress();
    
    // Debug logging
    console.log('🔍 [CONSULTATIVE] Context after update:', {
      stage: this.context.conversationStage,
      hasLocation: this.context.hasLocation,
      hasBudget: this.context.hasBudget,
      hasMotivation: this.context.hasMotivation,
      extractedCriteria: this.context.extractedCriteria,
      conversationDepth: this.context.conversationDepth,
      historyLength: this.conversationHistory.length,
      lastUserMessage: userMessage,
      lastMessages: this.conversationHistory.slice(-4).map(m => ({ role: m.role, content: m.content.substring(0, 50) + '...' }))
    });
    
    try {
      const aiResponse = await this.getConsultativeResponse(userMessage, hasEnoughContext);
      
      // Add AI response to history
      this.conversationHistory.push({
        role: 'assistant',
        content: aiResponse.message
      });
      
      // If user asked about properties, work with existing dataset instead of new search
      let mentionedProperties: PropertyResult[] = [];
      let skipBackendSearch = false; // Flag to prevent new searches
      
      if (this.lastSearchedProperties.length > 0 && this.isAskingAboutProperties(userMessage)) {
        console.log('🎯 [CONSULTATIVE] User asking about existing properties, using local dataset');
        skipBackendSearch = true; // Don't let backend do a new search
        
        // For property questions, we need to enhance the AI response to analyze the existing data
        // The AI already has the property context in the prompt, but we need to ensure it uses it
        
        // Extract any properties mentioned in the AI response
        mentionedProperties = this.extractMentionedProperties(aiResponse.message);
        
        // If AI didn't mention specific properties but should have, help it out
        if (mentionedProperties.length === 0) {
          console.log('🤔 [CONSULTATIVE] AI response lacks specific properties, analyzing question...');
          
          // For now, include the most relevant properties based on common queries
          // The AI should be doing this analysis, but we'll help ensure properties are shown
          const lowerMessage = userMessage.toLowerCase();
          
          // Default: show top 3 properties for any property-related question
          mentionedProperties = this.lastSearchedProperties.slice(0, 3);
          
          console.log('📍 [CONSULTATIVE] Including top properties for context:', 
            mentionedProperties.map(p => ({ address: p.address, price: p.price }))
          );
        }
        
        // Log what we're working with
        console.log('🏠 [CONSULTATIVE] Question type:', userMessage);
        console.log('🏠 [CONSULTATIVE] Working with', this.lastSearchedProperties.length, 'properties in dataset');
        console.log('🏠 [CONSULTATIVE] AI mentioned', mentionedProperties.length, 'specific properties');
      }

      // Log conversation to database (fire and forget)
      this.logConversation(userMessage, aiResponse.message).catch(err => 
        console.error('Failed to log conversation:', err)
      );

      // Get properties from backend response first
      let properties = aiResponse.properties || [];
      
      // If we have enough context but no properties, always search
      // This ensures properties are shown when the user provides criteria
      // BUT skip if user is asking about existing properties
      // ALSO search if user message contains clear property criteria
      const messageHasClearCriteria = this.messageContainsClearCriteria(userMessage);
      
      if ((hasEnoughContext || messageHasClearCriteria) && properties.length === 0 && !skipBackendSearch) {
        console.log('🔍 [CONSULTATIVE] Context is sufficient or clear criteria detected, searching for properties...');
        properties = await this.searchProperties();
      } else if (skipBackendSearch) {
        console.log('🚫 [CONSULTATIVE] Skipping backend search - working with existing dataset');
        // Don't fetch new properties, work with what we have
        properties = [];
      }
      
      // Store properties for AI analysis
      if (properties.length > 0) {
        this.lastSearchedProperties = properties;
        this.lastSearchedPropertiesForAI = this.convertPropertiesForAI(properties);
        console.log('📊 [CONSULTATIVE] Stored', properties.length, 'properties for AI analysis');
        
        // Debug: Log first property to verify data INCLUDING pool and yearBuilt
        if (properties[0]) {
          console.log('📊 [CONSULTATIVE] First property full data:', {
            address: properties[0].address,
            price: properties[0].price,
            sqft: properties[0].sqft,
            beds: properties[0].beds,
            pool: properties[0].pool,
            yearBuilt: properties[0].yearBuilt
          });
        }
        
        // Log properties with pools
        const propertiesWithPools = properties.filter(p => p.pool);
        console.log('🏊 [CONSULTATIVE] Properties with pools:', propertiesWithPools.length, 
          propertiesWithPools.map(p => ({ address: p.address, pool: p.pool })));
        
        // Log year built data
        const propertiesWithYearBuilt = properties.filter(p => p.yearBuilt);
        console.log('🏗️ [CONSULTATIVE] Properties with yearBuilt:', 
          propertiesWithYearBuilt.map(p => ({ address: p.address, yearBuilt: p.yearBuilt })));
      }

      // Always return properties if we have them
      // When user asks about existing properties, keep showing all of them
      // Only highlight the mentioned ones separately
      let propertiesToShow = properties;
      if (skipBackendSearch && properties.length === 0 && this.lastSearchedProperties.length > 0) {
        // User asked about existing properties - keep showing all of them
        propertiesToShow = this.lastSearchedProperties;
        console.log('🔄 [CONSULTATIVE] Keeping all', propertiesToShow.length, 'properties visible');
      }
      
      // KOPS-443: Handle AI-triggered searches
      // Check if backend already returned properties
      if (aiResponse.properties && aiResponse.properties.length > 0) {
        console.log('📦 [CONSULTATIVE] Backend executed search and returned', aiResponse.properties.length, 'properties');
        properties = aiResponse.properties;
        propertiesToShow = aiResponse.properties;
        
        // Update lastSearchedProperties so AI can answer questions about them
        this.lastSearchedProperties = aiResponse.properties;
        this.lastSearchedPropertiesForAI = this.convertPropertiesForAI(aiResponse.properties);
      }
      
      let finalProperties = propertiesToShow.length > 0 ? propertiesToShow : properties;
      let shouldTriggerSearch = false;
      
      // If AI triggered a search action and we don't have properties yet, search now
      if (aiResponse.searchAction && aiResponse.searchAction.trigger && finalProperties.length === 0 && !skipBackendSearch) {
        console.log('🔍 [CONSULTATIVE] AI requested search, executing now...');
        finalProperties = await this.searchProperties();
        shouldTriggerSearch = true;
        
        // Save search to history if we have a buyer ID
        const buyerContextStr = localStorage.getItem('portalBuyerContext');
        if (buyerContextStr && finalProperties.length > 0) {
          try {
            const buyerContext = JSON.parse(buyerContextStr);
            if (buyerContext.leadId) {
              // Save search history
              await axiosInstance.post('/lead-engagement/search/save', {
                buyerId: buyerContext.leadId,
                searchQuery: aiResponse.searchAction?.query || userMessage,
                searchCriteria: this.context.extractedCriteria,
                resultsCount: finalProperties.length,
                searchType: 'ai_conversation',
                sessionId: this.sessionId
              });
              console.log('💾 [CONSULTATIVE] Saved AI search to history');
            }
          } catch (error) {
            console.error('Failed to save search history:', error);
          }
        }
      }
      
      return {
        ...aiResponse,
        properties: finalProperties,
        mentionedProperties, // These are the specific ones to highlight (e.g., the largest)
        shouldSearchProperties: finalProperties.length > 0 || mentionedProperties.length > 0 || shouldTriggerSearch,
        hasEnoughContext,
        searchProgress,
        searchAction: aiResponse.searchAction, // Pass through for UI handling
        appointmentAction: aiResponse.appointmentAction // Pass through for UI handling
      };
    } catch (error: any) {
      console.error('❌ [CONSULTATIVE] Error in chat service:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // If it's a network error, provide more specific message
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
        return {
          message: "I'm having trouble connecting to the property search service. Please check your connection and try again.",
          shouldSearchProperties: false,
          hasEnoughContext: false,
          searchProgress
        };
      }
      
      // For other errors, log them but try to continue
      return {
        message: "I apologize, but I'm having trouble processing your request. Please try again in a moment.",
        shouldSearchProperties: false,
        hasEnoughContext: false,
        searchProgress
      };
    }
  }

  /**
   * Get context information for UI display
   */
  public getContext() {
    return {
      sessionId: this.sessionId,
      conversationDepth: this.context.conversationDepth,
      stage: this.context.conversationStage,
      extractedInfo: this.context.extractedCriteria,
      extractedCriteria: this.context.extractedCriteria, // Add alias for compatibility
      hasComprehensiveInfo: this.hasComprehensiveContext()
    };
  }

  /**
   * Trigger initial search for AISearchPortal with buyer context
   */
  public async triggerInitialSearch(): Promise<ChatResponse> {
    const isAISearchPortal = window.location.hostname.includes('aisearchportal.com');
    const buyerContextStr = localStorage.getItem('portalBuyerContext');
    const sanitizedContextStr = localStorage.getItem('portalBuyerContextSanitized');
    
    if (isAISearchPortal && buyerContextStr && (this as any).shouldAutoSearch) {
      try {
        const buyerContext = JSON.parse(buyerContextStr);
        const sanitizedContext = sanitizedContextStr ? JSON.parse(sanitizedContextStr) : buyerContext;
        console.log('🔍 [CONSULTATIVE] AISearchPortal - Triggering auto-search with buyer context');
        
        // Mark that we've triggered the auto-search
        (this as any).shouldAutoSearch = false;
        
        // Directly search for properties without conversational flow
        const properties = await this.searchProperties();
        
        if (properties && properties.length > 0) {
          // Create a response with the properties that acknowledges the agent relationship
          const agentName = buyerContext.agentInfo?.name || 'your agent';
          const buyerFirstName = buyerContext.name?.split(' ')[0] || 'there';
          
          const agentSearchHistory = (this as any).agentSearchHistory || [];
          let searchSummary = `Hi ${buyerFirstName}! I'm the AI Search Assistant working with ${agentName}, your real estate agent. `;
          
          if (agentSearchHistory.length > 0) {
            searchSummary += `${agentName} has been actively searching for properties for you. Recent searches include:\n`;
            agentSearchHistory.slice(0, 3).forEach((search: any) => {
              searchSummary += `• "${search.query}" (${search.resultsCount} properties found)\n`;
            });
            searchSummary += `\nWould you like to see the results from any of these searches, or shall I search for something new based on your preferences?`;
          } else {
            searchSummary += `${agentName} has shared your preferences with me, and I've found ${properties.length} properties in ${this.context.extractedCriteria.city} that match what you're looking for: ${this.context.extractedCriteria.bedrooms || this.context.extractedCriteria.beds} bedrooms, budget up to $${this.context.extractedCriteria.maxPrice?.toLocaleString()}. Let me show you what's available!`;
          }
          
          return {
            message: searchSummary,
            properties: properties,
            shouldSearchProperties: false,
            hasEnoughContext: true,
            searchProgress: {
              matchCount: properties.length,
              searchQuality: 'optimal'
            }
          };
        } else {
          const agentName = buyerContext.agentInfo?.name || 'your agent';
          const buyerFirstName = buyerContext.name?.split(' ')[0] || 'there';
          
          return {
            message: `Hi ${buyerFirstName}! I'm the AI Search Assistant working with ${agentName}, your real estate agent. I've searched based on the preferences ${agentName} shared with me, but I couldn't find any properties matching your exact criteria in ${this.context.extractedCriteria.city}. Would you like me to expand the search area or adjust any of your preferences?`,
            properties: [],
            shouldSearchProperties: false,
            hasEnoughContext: true
          };
        }
      } catch (error) {
        console.error('Failed to trigger auto-search:', error);
      }
    }
    
    // Return empty response for non-portal or no context
    return {
      message: '',
      shouldSearchProperties: false
    };
  }

  /**
   * Get consultative response from AI with enhanced "Otis-like" wrapper
   */
  private async getConsultativeResponse(userMessage: string, hasEnoughContext: boolean): Promise<ChatResponse> {
    try {
      // Build AI-enhanced prompt that includes all context
      const enhancedPrompt = this.buildAIEnhancedPrompt(userMessage);
      
      // Use the backend chat.realestate endpoint
      const aiServiceUrl = '/chat/public/realestate';
      // Debug conversation history
      console.log('📤 [CONSULTATIVE] Sending to backend:', {
        messageCount: this.conversationHistory.length,
        lastMessages: this.conversationHistory.slice(-3).map(m => ({
          role: m.role,
          contentPreview: m.content.substring(0, 100) + '...'
        }))
      });
      
      // Get agent info and buyer context if available
      let agentInfo = null;
      let buyerContext = null;
      const buyerContextStr = localStorage.getItem('portalBuyerContext');
      if (buyerContextStr) {
        try {
          buyerContext = JSON.parse(buyerContextStr);
          agentInfo = buyerContext.agentInfo;
        } catch (e) {
          console.error('Failed to parse buyer context:', e);
        }
      }
      
      const response = await axiosInstance.post(aiServiceUrl, {
        message: userMessage,
        messages: this.conversationHistory, // Send full conversation history!
        sessionId: this.sessionId,
        context: {
          source: 'chat.realestate',
          stage: this.context.conversationStage,
          extractedInfo: this.context.extractedCriteria,
          conversationDepth: this.context.conversationDepth,
          hasComprehensiveInfo: hasEnoughContext,
          lifestyle: this.context.extractedCriteria.lifestyle,
          motivation: this.context.extractedCriteria.motivation,
          timeline: this.context.extractedCriteria.timeline,
          aiEnhancedPrompt: enhancedPrompt, // Changed to aiEnhancedPrompt for compatibility
          agentInfo: agentInfo, // Pass agent info for appointment scheduling
          buyerContext: buyerContext // Pass full buyer context for search history
        }
      });
      
      const responseData = response.data;
      
      console.log('📡 [CONSULTATIVE] Backend response:', {
        hasResponse: !!responseData.response,
        hasSearchAction: !!responseData.searchAction,
        hasProperties: !!(responseData.properties && responseData.properties.length > 0),
        propertyCount: responseData.propertyCount,
        searchExecuted: responseData.searchExecuted
      });
      
      let aiResponse = responseData.response || responseData.message_text || responseData.message || '';
      
      // KOPS-443: Check if AI triggered a search action
      const searchAction = responseData.searchAction;
      if (searchAction && searchAction.trigger) {
        console.log('🤖 [CONSULTATIVE] AI triggered search action:', searchAction);
        
        // Update context with AI-extracted criteria
        if (searchAction.criteria) {
          // Merge AI criteria with existing context
          this.context.extractedCriteria = {
            ...this.context.extractedCriteria,
            ...searchAction.criteria
          };
          
          // Update context flags
          if (searchAction.criteria.city) this.context.hasLocation = true;
          if (searchAction.criteria.maxPrice || searchAction.criteria.minPrice) this.context.hasBudget = true;
          if (searchAction.criteria.beds || searchAction.criteria.features) this.context.hasFeatures = true;
          
          console.log('📝 [CONSULTATIVE] Updated context with AI criteria:', this.context.extractedCriteria);
        }
      }
      
      // Check if AI detected appointment intent
      const appointmentAction = responseData.appointmentAction;
      if (appointmentAction && appointmentAction.trigger) {
        console.log('📅 [CONSULTATIVE] AI detected appointment intent:', appointmentAction);
      }
      
      // Check if response contains property data
      const hasProperties = responseData.properties && responseData.properties.length > 0;
      
      // COMMENTED OUT: Let the natural AI response come through
      // Don't override the AI response - trust Claude/Bedrock to handle the conversation naturally
      /*
      // If we have properties but not enough context, hold them back
      if (hasProperties && !hasEnoughContext) {
        console.log('🎯 [CONSULTATIVE] Have properties but need more context first');
        return this.generateIntelligentResponse(userMessage);
      }
      
      // Check if AI understood the context and gave a natural response
      const isGenericResponse = this.isGenericResponse(aiResponse);
      
      if (isGenericResponse && this.context.conversationStage !== 'greeting') {
        console.log('🤖 [AI-ENHANCED] Response too generic, using intelligent fallback');
        // Use AI to generate a better response based on context
        return this.generateIntelligentResponse(userMessage);
      }
      */
      
      // Generate contextually aware suggested actions
      const suggestedActions = await this.generateAISuggestedActions();
      
      // AI decides if it's time to search based on conversation flow
      const shouldSearchProperties = hasEnoughContext && 
                                   (hasProperties || 
                                    this.aiShouldSearch(userMessage));
      
      return {
        message: aiResponse,
        shouldSearchProperties,
        suggestedActions,
        intent: responseData.intent,
        properties: hasProperties ? this.formatPropertiesFromBackend(responseData.properties) : undefined
      };
    } catch (error: any) {
      console.error('API Error:', error);
      // Even in error, try to maintain natural conversation flow
      return this.generateIntelligentResponse(userMessage);
    }
  }

  /**
   * Build AI-enhanced prompt with full context (like Otis would understand)
   */
  private buildAIEnhancedPrompt(userMessage: string): string {
    const criteria = this.context.extractedCriteria;
    const stage = this.context.conversationStage;
    
    let prompt = `You are having a natural conversation about real estate. The user has shared:
- Location preference: ${criteria.city || 'not specified yet'}
- Budget: ${criteria.maxPrice ? `up to $${criteria.maxPrice.toLocaleString()}` : 'not specified yet'}
- Home size: ${criteria.beds ? `${criteria.beds} bedrooms` : 'not specified yet'}
- Features desired: ${criteria.features?.join(', ') || 'not specified yet'}
- Motivation for moving: ${criteria.motivation || 'not shared yet'}
- Timeline: ${criteria.timeline || 'not discussed yet'}
- Lifestyle priorities: ${criteria.lifestyle?.join(', ') || 'not discovered yet'}

Current conversation stage: ${stage}
Conversation depth: ${this.context.conversationDepth} messages`;

    // Include property data if available and user is asking about properties
    if (this.lastSearchedPropertiesForAI.length > 0 && this.isAskingAboutProperties(userMessage)) {
      prompt += `\n\nIMPORTANT: The user is asking about the ${this.lastSearchedPropertiesForAI.length} properties currently shown. Here is the data:

${this.formatPropertiesForAIContext()}

When answering questions about these properties:
1. Always reference properties by their ADDRESS (not MLS number)
2. Include relevant details from the data (price, size, features)
3. If comparing properties, mention specific differences
4. If asked about features in descriptions, search the description text
5. Format prices nicely (e.g., $665,000 not 665000)

Example good response: "The largest home is 1128 E PHELPS Street with 3,067 sq ft. It's priced at $749,000 and has 5 bedrooms and 3 bathrooms."`;
    }

    prompt += `\n\nBased on this context and the user's latest message, respond naturally like a knowledgeable friend would. If they've given you enough information (location + budget + some preferences), you can offer to search. Otherwise, explore what matters most to them in their home search.

Remember: You're not just collecting data points - you're understanding their life situation and helping them find a home that fits their journey.`;

    return prompt;
  }

  /**
   * Check if response is too generic
   */
  private isGenericResponse(response: string): boolean {
    const genericPhrases = [
      "I'm here to help you find your perfect home! Could you tell me more",
      "What brings you to the real estate market today?",
      "Tell me about what you're looking for",
      "Can you share more details about"
    ];
    
    return genericPhrases.some(phrase => 
      response.toLowerCase().includes(phrase.toLowerCase())
    );
  }

  /**
   * Generate intelligent response when AI fails or is too generic
   */
  private async generateIntelligentResponse(userMessage: string): Promise<ChatResponse> {
    const stage = this.context.conversationStage;
    const criteria = this.context.extractedCriteria;
    
    // Build response based on what we know and what we need
    let message = '';
    
    if (stage === 'discovery' && criteria.city && criteria.maxPrice) {
      const location = criteria.city;
      const budget = criteria.maxPrice;
      
      message = `I love that you're looking in ${location} with a budget up to $${budget.toLocaleString()}. `;
      
      if (!criteria.motivation) {
        message += `What's prompting your move? Understanding your "why" helps me find homes that truly fit your needs.`;
      } else if (!criteria.beds) {
        message += `How many bedrooms would work best for your ${criteria.motivation === 'growing family' ? 'growing family' : 'situation'}?`;
      } else if (!criteria.features || criteria.features.length === 0) {
        message += `What features are must-haves for you? Maybe a pool, specific school districts, or a particular style of home?`;
      }
    } else if (stage === 'qualifying' && criteria.motivation) {
      message = `That makes perfect sense - ${this.getMotivationResponse(criteria.motivation)}. `;
      message += `What's your ideal timeline for making this move?`;
    } else if (stage === 'confirming') {
      message = this.buildConfirmationMessage();
    } else {
      // Fallback to template response but make it contextual
      return this.getConsultativeFallbackResponse(userMessage);
    }
    
    const suggestedActions = await this.generateAISuggestedActions();
    
    return {
      message,
      shouldSearchProperties: false,
      suggestedActions
    };
  }

  /**
   * Get response based on motivation
   */
  private getMotivationResponse(motivation: string): string {
    const responses: { [key: string]: string } = {
      'growing family': 'a growing family definitely needs more space to thrive',
      'downsizing': 'downsizing can be such a smart move for simplifying life',
      'job relocation': 'relocating for work is an exciting new chapter',
      'investment': 'real estate investment in this market can be a great opportunity',
      'lifestyle change': 'making a lifestyle change through your home choice is powerful'
    };
    
    return responses[motivation] || 'your situation is unique and I want to help find the perfect fit';
  }

  /**
   * AI-powered decision on whether to search
   */
  private aiShouldSearch(userMessage: string): boolean {
    const lowerMessage = userMessage.toLowerCase();
    
    // Direct search indicators
    const searchIndicators = [
      'show me', 'search', 'find', 'what\'s available',
      'let\'s see', 'ready to look', 'can you find'
    ];
    
    // Context-based decision
    if (searchIndicators.some(indicator => lowerMessage.includes(indicator))) {
      return true;
    }
    
    // If we have comprehensive context and user seems ready
    if (this.hasComprehensiveContext() && this.context.conversationDepth >= 3) {
      const readyIndicators = ['sounds good', 'that\'s right', 'exactly', 'yes', 'let\'s do it'];
      return readyIndicators.some(indicator => lowerMessage.includes(indicator));
    }
    
    return false;
  }

  /**
   * Generate AI-powered suggested actions based on conversation state
   */
  private async generateAISuggestedActions(): Promise<Array<{label: string; action: string}>> {
    const stage = this.context.conversationStage;
    const criteria = this.context.extractedCriteria;
    
    // Intelligent suggestions based on what's missing and what matters
    const suggestions = [];
    
    if (stage === 'greeting' || stage === 'discovery') {
      if (!criteria.motivation) {
        suggestions.push(
          { label: 'Growing our family', action: 'growing family' },
          { label: 'Want better schools', action: 'better schools for kids' },
          { label: 'Job relocation', action: 'relocating for work' },
          { label: 'Ready to downsize', action: 'downsizing' }
        );
      } else if (!criteria.timeline) {
        suggestions.push(
          { label: 'ASAP - urgent need', action: 'need to move immediately' },
          { label: 'Next 3-6 months', action: '3-6 months timeline' },
          { label: 'Just exploring options', action: 'just starting to look' },
          { label: 'When the right home appears', action: 'flexible timeline' }
        );
      }
    } else if (stage === 'qualifying') {
      if (!criteria.features || criteria.features.length < 2) {
        suggestions.push(
          { label: 'Must have a pool', action: 'pool is essential' },
          { label: 'Need home office space', action: 'work from home space' },
          { label: 'Want newer construction', action: 'prefer newer homes' },
          { label: 'Love outdoor living', action: 'big backyard for entertaining' }
        );
      }
    } else if (stage === 'confirming' || (this.hasComprehensiveContext() && !this.context.conversationDepth)) {
      suggestions.push(
        { label: 'Yes, search for homes!', action: 'ready to see properties' },
        { label: 'Tell me more about the area', action: 'what\'s the area like' },
        { label: 'Adjust my criteria', action: 'let me change something' }
      );
    }
    
    return suggestions.slice(0, 4); // Max 4 suggestions
  }

  /**
   * Build confirmation message summarizing what we understand
   */
  private buildConfirmationMessage(): string {
    const c = this.context.extractedCriteria;
    let summary = `Perfect! Let me make sure I have this right. You're looking for `;
    
    const parts = [];
    if (c.beds) parts.push(`a ${c.beds}-bedroom home`);
    if (c.city) parts.push(`in ${c.city}`);
    if (c.maxPrice) parts.push(`with a budget up to $${c.maxPrice.toLocaleString()}`);
    
    summary += parts.join(' ') + '. ';
    
    if (c.features && c.features.length > 0) {
      summary += `You want ${c.features.join(', ')}. `;
    }
    
    if (c.motivation) {
      summary += `This move is about ${c.motivation}. `;
    }
    
    summary += `\n\nShould I search for properties that match these criteria?`;
    
    return summary;
  }

  /**
   * Get consultative guidance for AI based on conversation stage
   */
  private getConsultativePrompt(): string {
    const stage = this.context.conversationStage;
    const criteria = this.context.extractedCriteria;
    
    switch(stage) {
      case 'greeting':
        return "Focus on understanding their motivation for moving. Ask about their current situation and what's prompting the change.";
      
      case 'discovery':
        return `They mentioned ${criteria.city || 'a location'} and ${criteria.beds || 'some'} bedrooms. Dig deeper into their lifestyle - daily routines, work situation, family dynamics. Don't search yet.`;
      
      case 'qualifying':
        return "Explore their timeline, budget comfort level, and any constraints. Ask about must-haves vs nice-to-haves. Still gathering context.";
      
      case 'confirming':
        return "Summarize what you've learned about their needs, motivations, and constraints. Ask if you've understood correctly before searching.";
      
      case 'searching':
        return "Now you can search for properties, but explain how each matches their stated lifestyle needs, not just features.";
      
      default:
        return "Continue building understanding of their needs and motivations.";
    }
  }

  /**
   * Generate consultative fallback response
   */
  private getConsultativeFallbackResponse(userMessage: string): ChatResponse {
    const stage = this.context.conversationStage;
    const criteria = this.context.extractedCriteria;
    
    // Stage-specific consultative responses
    if (stage === 'greeting' || this.context.conversationDepth === 1) {
      return {
        message: "I'd love to help you find your perfect home! Before we dive into specifics, can you tell me what's bringing you to the market? Are you relocating for work, looking for more space, or perhaps ready for a lifestyle change?",
        shouldSearchProperties: false,
        suggestedActions: [
          { label: "Relocating for work", action: "relocating_work" },
          { label: "Need more space", action: "need_space" },
          { label: "Lifestyle change", action: "lifestyle_change" },
          { label: "First time buyer", action: "first_time" }
        ]
      };
    }
    
    if (criteria.city && criteria.beds && !this.context.hasMotivation) {
      return {
        message: `I can see you're interested in ${criteria.beds}-bedroom homes in ${criteria.city}. That's a great area! Before I search, I'm curious - what's drawing you to ${criteria.city} specifically? And what about your current home isn't working for you anymore?`,
        shouldSearchProperties: false,
        suggestedActions: [
          { label: "Family is growing", action: "growing_family" },
          { label: "Want better schools", action: "schools_priority" },
          { label: "Closer to work", action: "commute_priority" },
          { label: "Love the area", action: "area_preference" }
        ]
      };
    }
    
    if (this.context.hasLocation && this.context.hasFeatures && !this.context.hasTimeline) {
      return {
        message: "This is really helpful! I'm getting a clear picture of what you're looking for. One more thing - what's your timeline? Are you looking to move soon, or are you just starting to explore options?",
        shouldSearchProperties: false,
        suggestedActions: [
          { label: "ASAP - Need to move soon", action: "timeline_asap" },
          { label: "Next 3-6 months", action: "timeline_3_6_months" },
          { label: "Just exploring options", action: "timeline_exploring" },
          { label: "Depends on finding the right place", action: "timeline_flexible" }
        ]
      };
    }
    
    if (this.hasComprehensiveContext()) {
      return {
        message: "Perfect! Based on everything you've shared about your situation and needs, I have a great understanding of what would work for your lifestyle. Let me search for homes that match not just your criteria, but your life goals...",
        shouldSearchProperties: true
      };
    }
    
    // Default consultative response
    return {
      message: "Tell me more about what's important to you in your next home. Beyond the basics like bedrooms and location, what would make a house truly feel like home for you?",
      shouldSearchProperties: false
    };
  }

  /**
   * Generate suggested actions based on conversation stage
   */
  private generateSuggestedActions(): Array<{label: string; action: string}> {
    const stage = this.context.conversationStage;
    
    switch(stage) {
      case 'greeting':
      case 'discovery':
        return [
          { label: "We're expecting a baby", action: "family_growing" },
          { label: "Job relocation", action: "job_relocation" },
          { label: "Want better schools", action: "schools_priority" },
          { label: "Looking to downsize", action: "downsizing" }
        ];
        
      case 'qualifying':
        return [
          { label: "Moving in next 3 months", action: "timeline_3_months" },
          { label: "Have flexibility on timing", action: "timeline_flexible" },
          { label: "Must have home office", action: "feature_home_office" },
          { label: "Pool is a must", action: "feature_pool" }
        ];
        
      case 'confirming':
        return [
          { label: "Yes, you understand perfectly", action: "confirm_understanding" },
          { label: "Let me clarify something", action: "clarify_needs" },
          { label: "Show me homes now", action: "ready_to_search" },
          { label: "I have another requirement", action: "add_requirement" }
        ];
        
      default:
        return [];
    }
  }

  /**
   * Update conversation context with deeper insights
   */
  private updateContext(message: string) {
    const lowerMessage = message.toLowerCase();
    
    // Extract location (existing code)
    const phoenixMetroCities = [
      'gilbert', 'scottsdale', 'phoenix', 'chandler', 'tempe', 'mesa',
      'glendale', 'peoria', 'surprise', 'queen creek', 'paradise valley',
      'cave creek', 'fountain hills', 'avondale', 'goodyear', 'buckeye',
      'gibert' // Common misspelling
    ];
    
    const cityRegex = new RegExp(`(${phoenixMetroCities.join('|')})`, 'i');
    const cityMatch = message.match(cityRegex);
    if (cityMatch) {
      let cityName = cityMatch[1].split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
      
      if (cityName.toLowerCase() === 'gibert') {
        cityName = 'Gilbert';
      }
      
      this.context.extractedCriteria.city = cityName;
      this.context.extractedCriteria.state = 'Arizona';
      this.context.hasLocation = true;
    }

    // Extract motivation and lifestyle
    if (lowerMessage.includes('baby') || lowerMessage.includes('growing family') || lowerMessage.includes('expecting')) {
      this.context.extractedCriteria.motivation = 'growing family';
      this.context.hasMotivation = true;
      if (!this.context.extractedCriteria.lifestyle) {
        this.context.extractedCriteria.lifestyle = [];
      }
      this.context.extractedCriteria.lifestyle.push('family-oriented');
    }
    
    if (lowerMessage.includes('school') || lowerMessage.includes('education')) {
      if (!this.context.extractedCriteria.lifestyle) {
        this.context.extractedCriteria.lifestyle = [];
      }
      this.context.extractedCriteria.lifestyle.push('education-focused');
      if (!this.context.extractedCriteria.constraints) {
        this.context.extractedCriteria.constraints = [];
      }
      this.context.extractedCriteria.constraints.push('good schools');
    }
    
    if (lowerMessage.includes('work from home') || lowerMessage.includes('remote') || lowerMessage.includes('home office')) {
      if (!this.context.extractedCriteria.lifestyle) {
        this.context.extractedCriteria.lifestyle = [];
      }
      this.context.extractedCriteria.lifestyle.push('remote-work');
      if (!this.context.extractedCriteria.features) {
        this.context.extractedCriteria.features = [];
      }
      this.context.extractedCriteria.features.push('home office');
    }
    
    // Extract timeline
    if (lowerMessage.includes('asap') || lowerMessage.includes('urgent') || lowerMessage.includes('immediately')) {
      this.context.extractedCriteria.timeline = 'immediate';
      this.context.hasTimeline = true;
    } else if (lowerMessage.includes('3 months') || lowerMessage.includes('three months')) {
      this.context.extractedCriteria.timeline = '3 months';
      this.context.hasTimeline = true;
    } else if (lowerMessage.includes('6 months') || lowerMessage.includes('six months')) {
      this.context.extractedCriteria.timeline = '6 months';
      this.context.hasTimeline = true;
    } else if (lowerMessage.includes('exploring') || lowerMessage.includes('just looking')) {
      this.context.extractedCriteria.timeline = 'exploring';
      this.context.hasTimeline = true;
    }
    
    // Extract constraints
    if (lowerMessage.includes('no hoa') || lowerMessage.includes('avoid hoa')) {
      if (!this.context.extractedCriteria.constraints) {
        this.context.extractedCriteria.constraints = [];
      }
      this.context.extractedCriteria.constraints.push('no HOA');
      this.context.hasConstraints = true;
    }
    
    if (lowerMessage.includes('quiet') || lowerMessage.includes('peaceful')) {
      if (!this.context.extractedCriteria.lifestyle) {
        this.context.extractedCriteria.lifestyle = [];
      }
      this.context.extractedCriteria.lifestyle.push('quiet neighborhood');
    }

    // Extract price with enhanced format detection
    // Look for prices with $ sign OR preceded by price keywords
    const pricePatterns = [
      // Dollar sign prices: $950,000, $950k, $1.5M
      /\$[\d,]+\.?\d*\s*(?:k|K|m|M|mil|million)?/i,
      // Price keywords followed by number: "budget 950000", "under 950k"
      /(?:budget|price|under|below|max|maximum|up to|less than|around|about)\s+(?:is\s+)?(?:of\s+)?[\d,]+\.?\d*\s*(?:k|K|m|M|mil|million)?/i,
      // Number followed by price keywords: "950k budget", "950000 max"
      /[\d,]+\.?\d*\s*(?:k|K|m|M|mil|million)?\s+(?:budget|max|maximum)/i,
      // Standalone large numbers (likely prices in real estate context): 975000, 975,000
      /^[\d,]{6,}$/i
    ];
    
    let priceMatch = null;
    for (const pattern of pricePatterns) {
      const match = message.match(pattern);
      if (match) {
        priceMatch = match;
        break;
      }
    }
    
    if (priceMatch) {
      // Check if it's a rental price
      if (lowerMessage.includes('per month') || lowerMessage.includes('/month') || 
          lowerMessage.includes('monthly') || lowerMessage.includes('rent')) {
        console.log('🏠 [PRICE] Detected rental price, skipping for home sales search');
        return; // Skip rental prices for home sales
      }
      
      // Extract just the numeric part from the full match
      const fullMatch = priceMatch[0];
      const numericMatch = fullMatch.match(/[\d,]+\.?\d*/);
      if (!numericMatch) return;
      
      const priceStr = numericMatch[0].replace(/,/g, '');
      let price = parseFloat(priceStr);
      
      // Handle different formats by checking the full match
      const lowerMatch = fullMatch.toLowerCase();
      if (lowerMatch.includes('k')) {
        price *= 1000;
      } else if (lowerMatch.includes('m') && !lowerMatch.includes('mil')) {
        // Handle $1.5M format
        price *= 1000000;
      } else if (lowerMatch.includes('mil') || lowerMatch.includes('million')) {
        price *= 1000000;
      }
      
      // Sanity check for real estate prices - if it's over $50M, it's probably a parsing error
      if (price > 50000000 && priceStr.length > 7) {
        // Likely parsed extra digits, try to fix common cases like "900,000" being read as "900000000"
        const digitCount = priceStr.length;
        if (digitCount === 9 && priceStr.startsWith('900')) {
          // This is likely "900,000" misread as "900000000"
          price = 900000;
          console.log('💰 [PRICE] Fixed price parsing error: 900000000 -> 900000');
        }
      }
      
      // Determine if it's a max price or price range
      const lowerMsg = message.toLowerCase();
      if (lowerMsg.includes('under') || lowerMsg.includes('below') || lowerMsg.includes('max') || 
          lowerMsg.includes('budget') || lowerMsg.includes('up to') || lowerMsg.includes('less than')) {
        // User specified a maximum budget
        this.context.extractedCriteria.maxPrice = price;
        // Set minimum to 85% of max - keeps properties in similar tier
        this.context.extractedCriteria.minPrice = Math.floor(price * 0.85);
      } else if (lowerMsg.includes('above') || lowerMsg.includes('over') || lowerMsg.includes('minimum') || 
                 lowerMsg.includes('at least') || lowerMsg.includes('more than')) {
        // User specified a minimum price
        this.context.extractedCriteria.minPrice = price;
        // Set a reasonable maximum (120% of min) for luxury searches
        this.context.extractedCriteria.maxPrice = Math.ceil(price * 1.2);
      } else if (lowerMsg.includes('around') || lowerMsg.includes('about') || lowerMsg.includes('approximately')) {
        // User specified an approximate price - search 10% above and below
        this.context.extractedCriteria.minPrice = Math.floor(price * 0.9);
        this.context.extractedCriteria.maxPrice = Math.ceil(price * 1.1);
      } else {
        // Default: treat single price as a budget (maximum)
        this.context.extractedCriteria.maxPrice = price;
        this.context.extractedCriteria.minPrice = Math.floor(price * 0.85);
      }
      this.context.hasBudget = true;
      
      // Debug logging for price extraction
      console.log('💰 [PRICE] Extracted price:', {
        originalMessage: message,
        fullMatch: fullMatch,
        numericValue: priceStr,
        parsedPrice: price,
        minPrice: this.context.extractedCriteria.minPrice,
        maxPrice: this.context.extractedCriteria.maxPrice
      });
    }

    // Extract bedrooms and bathrooms (existing code)
    const bedroomRegex = /(\d+)\s*(?:bed|bedroom|br)/i;
    const bedroomMatch = message.match(bedroomRegex);
    if (bedroomMatch) {
      this.context.extractedCriteria.beds = parseInt(bedroomMatch[1]);
      this.context.extractedCriteria.bedrooms = parseInt(bedroomMatch[1]);
      this.context.hasFeatures = true;
    }

    const bathroomRegex = /(\d+)\s*(?:bath|bathroom|ba)/i;
    const bathroomMatch = message.match(bathroomRegex);
    if (bathroomMatch) {
      this.context.extractedCriteria.baths = parseInt(bathroomMatch[1]);
      this.context.extractedCriteria.bathrooms = parseInt(bathroomMatch[1]);
      this.context.hasFeatures = true;
    }
    
    // Extract square footage
    const sqftRegex = /(\d{3,5})\s*(?:sq\.?\s*f(?:ee|oo)?t|square\s*f(?:ee|oo)?t|sf)/i;
    const sqftMatch = message.match(sqftRegex);
    if (sqftMatch) {
      const sqft = parseInt(sqftMatch[1]);
      // If they say "around 3200", set a range
      if (lowerMessage.includes('around') || lowerMessage.includes('about') || lowerMessage.includes('approximately')) {
        this.context.extractedCriteria.minSqft = sqft - 200;
        this.context.extractedCriteria.maxSqft = sqft + 200;
        console.log('📐 [CONSULTATIVE] Extracted sqft range:', this.context.extractedCriteria.minSqft, '-', this.context.extractedCriteria.maxSqft);
      } else if (lowerMessage.includes('at least') || lowerMessage.includes('minimum')) {
        this.context.extractedCriteria.minSqft = sqft;
        console.log('📐 [CONSULTATIVE] Extracted min sqft:', sqft);
      } else {
        // Default to setting as minimum
        this.context.extractedCriteria.minSqft = sqft;
        console.log('📐 [CONSULTATIVE] Extracted sqft (as min):', sqft);
      }
      this.context.hasFeatures = true;
    }

    // Extract year built preferences
    const yearBuiltRegex = /built\s*(?:no\s*later\s*than|before|after|in)?\s*(\d{4})/i;
    const yearBuiltMatch = message.match(yearBuiltRegex);
    if (yearBuiltMatch) {
      const year = parseInt(yearBuiltMatch[1]);
      if (lowerMessage.includes('no later than') || lowerMessage.includes('before')) {
        this.context.extractedCriteria.maxYearBuilt = year;
      } else if (lowerMessage.includes('after')) {
        this.context.extractedCriteria.minYearBuilt = year;
      } else {
        this.context.extractedCriteria.yearBuilt = year;
      }
      this.context.hasFeatures = true;
    }
    
    // Handle "newer homes" preference
    if (lowerMessage.includes('newer home') || lowerMessage.includes('new home') || 
        lowerMessage.includes('recently built') || lowerMessage.includes('new construction')) {
      this.context.extractedCriteria.minYearBuilt = new Date().getFullYear() - 10; // Last 10 years
      this.context.hasFeatures = true;
      if (!this.context.extractedCriteria.features) {
        this.context.extractedCriteria.features = [];
      }
      this.context.extractedCriteria.features.push('newer construction');
      console.log('🏗️ [NEWER HOMES] User prefers newer homes, setting minYearBuilt:', this.context.extractedCriteria.minYearBuilt);
    }
    
    // Extract features (existing code with lifestyle additions)
    const features = [];
    if (lowerMessage.includes('pool')) {
      features.push('pool');
      if (!this.context.extractedCriteria.lifestyle) {
        this.context.extractedCriteria.lifestyle = [];
      }
      this.context.extractedCriteria.lifestyle.push('outdoor living');
    }
    if (lowerMessage.includes('garage')) features.push('garage');
    if (lowerMessage.includes('yard')) features.push('yard');
    if (lowerMessage.includes('modern')) features.push('modern');
    if (lowerMessage.includes('single story') || lowerMessage.includes('single level') || 
        lowerMessage.includes('one story') || lowerMessage.includes('1 story')) {
      features.push('single story');
    }
    if (lowerMessage.includes('good schools') || lowerMessage.includes('great schools') || 
        lowerMessage.includes('top schools') || lowerMessage.includes('best schools')) {
      features.push('good schools');
      if (!this.context.extractedCriteria.lifestyle) {
        this.context.extractedCriteria.lifestyle = [];
      }
      this.context.extractedCriteria.lifestyle.push('education-focused');
    }
    
    if (features.length > 0) {
      this.context.extractedCriteria.features = features;
      this.context.hasFeatures = true;
    }

    // Extract purpose and update motivation
    if (lowerMessage.includes('upgrade') || lowerMessage.includes('bigger')) {
      this.context.extractedCriteria.purpose = 'upgrade';
      this.context.extractedCriteria.motivation = 'need more space';
      this.context.hasMotivation = true;
    } else if (lowerMessage.includes('downsize') || lowerMessage.includes('downsizing')) {
      this.context.extractedCriteria.purpose = 'downsize';
      this.context.extractedCriteria.motivation = 'downsizing';
      this.context.hasMotivation = true;
      if (!this.context.extractedCriteria.lifestyle) {
        this.context.extractedCriteria.lifestyle = [];
      }
      this.context.extractedCriteria.lifestyle.push('simplified living');
    } else if (lowerMessage.includes('investment')) {
      this.context.extractedCriteria.purpose = 'investment';
      this.context.extractedCriteria.motivation = 'investment opportunity';
      this.context.hasMotivation = true;
    }

    // Update conversation stage based on depth and context
    this.updateConversationStage();
  }

  /**
   * Update conversation stage based on collected information
   */
  private updateConversationStage() {
    // Check if user provided complete criteria upfront
    if (this.hasCompleteSearchCriteria()) {
      this.context.conversationStage = 'searching';
      console.log('🚀 [CONSULTATIVE] Complete criteria detected - jumping to searching stage');
      return;
    }
    
    // Otherwise follow consultative flow
    const hasBasicInfo = this.context.hasLocation || this.context.hasBudget || this.context.hasFeatures;
    const hasLifestyleInfo = this.context.hasMotivation || 
                            (this.context.extractedCriteria.lifestyle && this.context.extractedCriteria.lifestyle.length > 0);
    const hasTimingInfo = this.context.hasTimeline || this.context.hasConstraints;
    
    if (this.context.conversationDepth === 1 && !hasBasicInfo) {
      this.context.conversationStage = 'greeting';
    } else if (hasBasicInfo && !hasLifestyleInfo) {
      this.context.conversationStage = 'discovery';
    } else if (hasBasicInfo && hasLifestyleInfo && !hasTimingInfo) {
      this.context.conversationStage = 'qualifying';
    } else if (hasBasicInfo && hasLifestyleInfo && hasTimingInfo) {
      this.context.conversationStage = 'confirming';
    } else if (this.hasComprehensiveContext()) {
      this.context.conversationStage = 'searching';
    }
  }

  /**
   * Check if message contains clear property search criteria
   */
  private messageContainsClearCriteria(message: string): boolean {
    const lowerMessage = message.toLowerCase();
    
    // Check for location + price pattern
    const hasPricePattern = /under \$?\d+k?|under \d+,?\d+|less than \$?\d+k?|\$?\d+k? or less|around \$?\d+k?|\d{3},\d{3}|\d+k/i.test(message);
    const hasLocationPattern = /\b(phoenix|scottsdale|tempe|chandler|gilbert|mesa|glendale|peoria|surprise|queen creek)\b/i.test(message);
    const hasPropertyFeatures = /\b\d+\s*(bed|br|bedroom|bath|ba|bathroom|car\s*garage)\b/i.test(message);
    const hasSpecialFeatures = /\b(pool|golf|gated|mountain view|city view|waterfront|no hoa)\b/i.test(message);
    
    // NEW: Check for square footage patterns
    const hasSquareFootage = /\b\d{3,4}\s*(sq\.?\s*f(?:ee|oo)?t|square\s*f(?:ee|oo)?t|sf)\b/i.test(message);
    
    // NEW: Check for property condition/age patterns
    const hasConditionCriteria = /\b(newer|new|move.?in.?ready|updated|modern|renovated|remodeled)\b/i.test(message);
    
    // NEW: Check for size descriptors
    const hasSizeCriteria = /\b(larger|bigger|more space|spacious|smaller)\b/i.test(message);
    
    // If message has location + (price OR features), it's clear criteria
    if (hasLocationPattern && (hasPricePattern || hasPropertyFeatures || hasSpecialFeatures)) {
      console.log('🎯 [CONSULTATIVE] Clear search criteria detected: location + features');
      return true;
    }
    
    // NEW: If message mentions specific square footage, that's clear criteria
    if (hasSquareFootage) {
      console.log('🎯 [CONSULTATIVE] Clear search criteria detected: square footage requirement');
      return true;
    }
    
    // NEW: If message mentions changing search area with "other areas" or specific new locations
    if (lowerMessage.includes('other area') || lowerMessage.includes('look in') || lowerMessage.includes('what about')) {
      console.log('🎯 [CONSULTATIVE] Clear search criteria detected: area change request');
      return true;
    }
    
    // NEW: If message has multiple criteria together (e.g., "3200 sq foot newer move in ready")
    const criteriaCount = [hasSquareFootage, hasConditionCriteria, hasSizeCriteria, hasPropertyFeatures, hasSpecialFeatures].filter(Boolean).length;
    if (criteriaCount >= 2) {
      console.log('🎯 [CONSULTATIVE] Clear search criteria detected: multiple criteria (', criteriaCount, ')');
      return true;
    }
    
    // Check for "show me" or "find me" patterns with specifics
    const hasSearchIntent = /^(show me|find me|search for|look for|i want|i need|looking for|i am open)/i.test(lowerMessage);
    const hasSpecifics = hasPropertyFeatures || hasPricePattern || hasLocationPattern || hasSquareFootage || hasConditionCriteria;
    
    return hasSearchIntent && hasSpecifics;
  }

  /**
   * Check if we have comprehensive context for property search
   */
  private hasComprehensiveContext(): boolean {
    // Check if user provided complete search criteria in one message
    const hasCompleteInitialCriteria = this.hasCompleteSearchCriteria();
    if (hasCompleteInitialCriteria) {
      console.log('🔍 [CONSULTATIVE] User provided complete criteria upfront - allowing immediate search');
      return true;
    }
    
    // Otherwise, require consultative conversation flow
    const hasEssentials = this.context.hasLocation && this.context.hasBudget;
    const hasDepth = this.context.hasMotivation || this.context.hasTimeline || 
                    (this.context.extractedCriteria.lifestyle && this.context.extractedCriteria.lifestyle.length > 0);
    const hasMinimumConversation = this.context.conversationDepth >= 3;
    
    return Boolean(hasEssentials && hasDepth && hasMinimumConversation);
  }
  
  /**
   * Check if user provided complete search criteria (location + budget + property details)
   */
  private hasCompleteSearchCriteria(): boolean {
    const c = this.context.extractedCriteria;
    
    // Must have location (budget is optional if other criteria exist)
    if (!this.context.hasLocation) {
      return false;
    }
    
    // Must have property specifications (beds or baths or features)
    const hasPropertySpecs = Boolean(
      c.beds || 
      c.baths || 
      (c.features && c.features.length > 0) ||
      c.propertyType
    );
    
    // If they have location + budget + property specs, that's complete enough to search
    return hasPropertySpecs;
  }

  /**
   * Check if user explicitly requests search
   */
  private userRequestsSearch(message: string): boolean {
    const searchPhrases = [
      'show me homes',
      'search for properties',
      'find properties',
      'what\'s available',
      'show me what you found',
      'let\'s see homes',
      'ready to see properties'
    ];
    
    const lowerMessage = message.toLowerCase();
    return searchPhrases.some(phrase => lowerMessage.includes(phrase));
  }

  /**
   * Search properties using Theta AI
   */
  protected async searchProperties(): Promise<PropertyResult[]> {
    try {
      // KOPS-443: Use the user's actual message for Theta AI, not reconstructed query
      // This preserves subdivision names like "Freeman Farms" that Theta AI understands
      const lastUserMessage = this.conversationHistory
        .filter(m => m.role === 'user')
        .slice(-1)[0]?.content || '';
      
      // Only fall back to constructed query if we don't have a user message
      const searchQuery = lastUserMessage || this.buildEnhancedSearchQuery();
      
      console.log('🔍 [SEARCH] Searching properties with:', {
        originalUserMessage: lastUserMessage,
        searchQuery: searchQuery,
        structuredCriteria: this.context.extractedCriteria
      });
      
      const response = await axiosInstance.post('/theta-ai/search', {
        query: searchQuery,
        sessionId: this.sessionId,
        userId: 'public-user',
        criteria: {
          minPrice: this.context.extractedCriteria.minPrice,
          maxPrice: this.context.extractedCriteria.maxPrice,
          bedrooms: this.context.extractedCriteria.beds,  // Changed from 'beds' to 'bedrooms' to match API
          bathrooms: this.context.extractedCriteria.baths, // Changed from 'baths' to 'bathrooms' to match API
          city: this.context.extractedCriteria.city,
          state: this.context.extractedCriteria.state,
          features: this.context.extractedCriteria.features,
          propertyType: this.context.extractedCriteria.propertyType
        },
        context: {
          lifestyle: this.context.extractedCriteria.lifestyle,
          motivation: this.context.extractedCriteria.motivation,
          constraints: this.context.extractedCriteria.constraints
        }
      }).catch(error => {
        console.error('Theta AI search failed:', error);
        return { data: { properties: [] } };
      });
      
      if (response.data.properties && Array.isArray(response.data.properties)) {
        return this.formatPropertiesWithContext(response.data.properties);
      }
      
      return [];
    } catch (error) {
      console.error('Property search error:', error);
      return [];
    }
  }

  /**
   * Build enhanced search query with lifestyle context
   */
  private buildEnhancedSearchQuery(): string {
    const criteria = this.context.extractedCriteria;
    const parts = [];
    
    // Basic criteria
    if (criteria.beds) {
      parts.push(`${criteria.beds} bedroom`);
    }
    
    if (criteria.baths) {
      parts.push(`${criteria.baths} bathroom`);
    }
    
    parts.push('homes');
    
    if (criteria.city) {
      parts.push(`in ${criteria.city}`);
    }
    
    if (criteria.maxPrice) {
      parts.push(`under $${criteria.maxPrice}`);
    }
    
    // Enhanced lifestyle criteria
    if (criteria.lifestyle?.includes('family-oriented')) {
      parts.push('in family-friendly neighborhoods');
    }
    
    if (criteria.lifestyle?.includes('education-focused')) {
      parts.push('near highly-rated schools');
    }
    
    if (criteria.features && criteria.features.length > 0) {
      parts.push(`with ${criteria.features.join(', ')}`);
    }
    
    // Timeline urgency
    if (criteria.timeline === 'immediate') {
      parts.push('available now');
    }
    
    if (criteria.maxYearBuilt) {
      parts.push(`built no later than ${criteria.maxYearBuilt}`);
    } else if (criteria.minYearBuilt) {
      parts.push(`built after ${criteria.minYearBuilt}`);
    }
    
    return parts.join(' ');
  }

  /**
   * Format properties from backend response
   */
  private formatPropertiesFromBackend(properties: any[]): PropertyResult[] {
    return properties.map((prop, index) => {
      // Handle the address object properly
      let addressString = '';
      if (prop.address && typeof prop.address === 'object') {
        // If address is an object with street, city, state, zip
        addressString = prop.address.street || 
                       `${prop.address.city || ''}, ${prop.address.state || 'AZ'} ${prop.address.zip || ''}`.trim();
      } else if (typeof prop.address === 'string') {
        addressString = prop.address;
      } else {
        addressString = prop.UnparsedAddress || prop.StreetAddress || 'Address not available';
      }
      
      return {
        id: prop.id || prop.ListingId || prop.mlsNumber || `prop-${index}`,
        mlsNumber: prop.mlsNumber || prop.ListingId || prop.id || `MLS-${index}`,
        address: addressString, // Use the formatted string, not the object
        city: prop.city || prop.City || prop.address?.city || '',
        state: prop.state || prop.StateOrProvince || prop.address?.state || 'AZ',
        zipCode: prop.zipCode || prop.PostalCode || prop.address?.zipCode || prop.address?.zip || '',
        price: prop.price || prop.ListPrice || prop.CurrentPrice || 0,
        beds: prop.bedrooms || prop.BedroomsTotal || prop.beds || 0,
        baths: prop.bathrooms || prop.BathroomsFull || prop.baths || 0,
        sqft: prop.sqft || prop.squareFeet || prop.LivingArea || prop.BuildingAreaTotal || 0,
        images: prop.images || prop.Media?.map((m: any) => m.MediaURL) || [],
        latitude: prop.latitude || prop.Latitude || prop.address?.latitude,
        longitude: prop.longitude || prop.Longitude || prop.address?.longitude,
        listingBrokerage: prop.listingBrokerage || prop.listingOffice || prop.ListOfficeName || prop.ListingOfficeName || prop.OfficeName,
        listingAgent: prop.listingAgent || prop.ListAgentFullName || prop.ListingAgentName || prop.AgentName,
        description: prop.description || prop.PublicRemarks || prop.propertyDescription || '',
        whyItMatches: prop.whyItMatches || this.generateLifestyleMatchReason(prop)
      };
    });
  }

  /**
   * Format properties with lifestyle context
   */
  private formatPropertiesWithContext(thetaProperties: any[]): PropertyResult[] {
    return thetaProperties.slice(0, 7).map((prop, index) => {
      const formatted = this.formatSingleProperty(prop, index);
      formatted.whyItMatches = this.generateLifestyleMatchReason(prop);
      return formatted;
    });
  }

  /**
   * Format single property (extracted for reuse)
   */
  private formatSingleProperty(prop: any, index: number): PropertyResult {
    const address = prop.address?.street || 
                   prop.UnparsedAddress || 
                   prop.StreetAddress || 
                   'Address not available';
    
    // Log to debug image and brokerage info
    console.log('🏠 [CHAT] Formatting property:', {
      id: prop.id,
      hasImages: !!prop.images,
      imageCount: prop.images?.length || 0,
      firstImage: prop.images?.[0],
      listingBrokerage: prop.listingBrokerage || prop.ListOfficeName
    });
    
    const baseProperty = {
      id: prop.id || prop.ListingId || prop.mlsNumber || `prop-${index}`,
      mlsNumber: prop.mlsNumber || prop.ListingId || prop.id || `MLS-${index}`,
      address: address,
      city: prop.city || prop.City || prop.address?.city || '',
      state: prop.state || prop.StateOrProvince || prop.address?.state || 'AZ',
      zipCode: prop.zipCode || prop.PostalCode || prop.address?.zipCode || '',
      price: prop.price || prop.ListPrice || prop.CurrentPrice || 0,
      beds: prop.bedrooms || prop.BedroomsTotal || prop.beds || 0,
      baths: prop.bathrooms || prop.BathroomsFull || prop.baths || 0,
      sqft: prop.sqft || prop.squareFeet || prop.LivingArea || prop.BuildingAreaTotal || 0,
      images: prop.images || prop.Media?.map((m: any) => m.MediaURL) || [],
      latitude: prop.latitude || prop.Latitude || prop.address?.latitude,
      longitude: prop.longitude || prop.Longitude || prop.address?.longitude,
      listingBrokerage: prop.listingBrokerage || prop.listingOffice || prop.ListOfficeName || prop.ListingOfficeName || prop.OfficeName,
      listingAgent: prop.listingAgent || prop.ListAgentFullName || prop.ListingAgentName || prop.AgentName,
      description: prop.description || prop.PublicRemarks || prop.propertyDescription || '',
      // Rich MLS data for AI analysis
      yearBuilt: prop.yearBuilt || prop.YearBuilt,
      daysOnMarket: prop.daysOnMarket || prop.DaysOnMarket || prop.DOM,
      propertyType: prop.propertyType || prop.PropertyType || prop.PropertySubType,
      features: prop.features || prop.Features || [],
      hoaFee: prop.hoaFee || prop.AssociationFee || prop.HOAFee || prop.associationFee,
      lotSize: prop.lotSize || prop.LotSizeSquareFeet || prop.LotSizeAcres,
      garage: prop.garage || prop.GarageSpaces || prop.ParkingTotal,
      pool: prop.pool || prop.PoolPrivateYN === 'Y' || prop.PoolFeatures?.length > 0,
      pricePerSqft: prop.pricePerSqft || (prop.price && prop.sqft ? Math.round(prop.price / prop.sqft) : undefined),
      architecturalStyle: prop.architecturalStyle || prop.ArchitecturalStyle,
      viewType: prop.view || prop.View,
      communityAmenities: prop.communityAmenities || prop.CommunityFeatures || [],
      // Additional fields from backend
      subdivision: prop.subdivision,
      schoolDistrict: prop.schoolDistrict,
      elementarySchool: prop.elementarySchool,
      virtualTour: prop.virtualTour,
      mlsArea: prop.mlsArea,
      fireplace: prop.fireplace,
      listingDate: prop.listingDate,
      status: prop.status
    };
    
    return baseProperty;
  }

  /**
   * Convert properties to lightweight format for AI analysis
   */
  private convertPropertiesForAI(properties: PropertyResult[]): PropertyForAI[] {
    return properties.map(prop => ({
      id: prop.id,
      mlsNumber: prop.mlsNumber,
      address: prop.address,
      city: prop.city || '',
      price: prop.price,
      beds: prop.beds,
      baths: prop.baths,
      sqft: prop.sqft,
      yearBuilt: prop.yearBuilt,
      daysOnMarket: prop.daysOnMarket,
      propertyType: prop.propertyType,
      features: prop.features,
      description: prop.description,
      hoaFee: prop.hoaFee,
      lotSize: prop.lotSize,
      garage: prop.garage,
      pool: prop.pool,
      pricePerSqft: prop.pricePerSqft,
      architecturalStyle: prop.architecturalStyle,
      viewType: prop.viewType,
      masterPlanned: prop.communityAmenities?.includes('Master Planned'),
      communityAmenities: prop.communityAmenities,
      fireplace: prop.fireplace,
      // Enhanced fields for comprehensive responses
      subdivision: prop.subdivision,
      schoolDistrict: prop.schoolDistrict,
      priceReduction: (prop as any).priceReduction || null,
      listingDate: prop.listingDate,
      modifiedDate: (prop as any).modifiedDate || null,
      virtualTour: prop.virtualTour,
      spa: (prop as any).spa || null,
      conditionInsights: (prop as any).conditionInsights || null,
      mlsArea: prop.mlsArea,
      status: prop.status,
      allFeatures: prop.features || [],
      // School ratings would need to be added from another source
      schoolRatings: undefined,
      // Additional computed fields
      taxAssessedValue: undefined, // Would need tax data
      recentRenovations: this.extractRenovationsFromDescription(prop.description)
    }));
  }
  
  /**
   * Extract renovation info from property description
   */
  private extractRenovationsFromDescription(description?: string): string[] | undefined {
    if (!description) return undefined;
    
    const renovationKeywords = [
      'updated', 'renovated', 'remodeled', 'new kitchen', 'new bathroom',
      'new roof', 'new hvac', 'new floors', 'upgraded', 'recently painted'
    ];
    
    const found: string[] = [];
    const lowerDesc = description.toLowerCase();
    
    renovationKeywords.forEach(keyword => {
      if (lowerDesc.includes(keyword)) {
        found.push(keyword);
      }
    });
    
    return found.length > 0 ? found : undefined;
  }
  
  /**
   * Check if user is asking about properties
   */
  private isAskingAboutProperties(userMessage: string): boolean {
    const lowerMessage = userMessage.toLowerCase();
    
    // If we have properties loaded and user is asking ANY question that could relate to them
    if (this.lastSearchedProperties.length > 0) {
      // Keywords that indicate property questions
      const propertyQuestions = [
        'which', 'what', 'how', 'tell me', 'show me', 'list',
        'property', 'properties', 'home', 'homes', 'house', 'houses',
        'option', 'options', 'choice', 'choices', 'these', 'those',
        'best', 'worst', 'recommend', 'suggestion', 'advice',
        'deal', 'negotiate', 'work with', 'seller',
        'hoa', 'fee', 'fees', 'cost', 'costs',
        'pool', 'garage', 'yard', 'view', 'feature',
        'size', 'sqft', 'square', 'large', 'small',
        'price', 'cheap', 'expensive', 'affordable',
        'new', 'old', 'built', 'year', 'age',
        'bedroom', 'bathroom', 'bed', 'bath',
        'compare', 'difference', 'between', 'versus',
        'neighborhood', 'area', 'location', 'school',
        'any', 'all', 'none', 'some'
      ];
      
      // Also check for questions about the current results
      const contextualPhrases = [
        'from these', 'of these', 'in this list', 'that you found',
        'you showed', 'we found', 'search results', 'current listings'
      ];
      
      // Check if message contains property-related keywords
      const hasPropertyKeyword = propertyQuestions.some(keyword => lowerMessage.includes(keyword));
      const hasContextualPhrase = contextualPhrases.some(phrase => lowerMessage.includes(phrase));
      
      // If asking a question and we have properties loaded, assume it's about the properties
      const isQuestion = lowerMessage.includes('?') || 
                        lowerMessage.startsWith('which') || 
                        lowerMessage.startsWith('what') || 
                        lowerMessage.startsWith('how') ||
                        lowerMessage.startsWith('tell me') ||
                        lowerMessage.startsWith('show me');
      
      const matches = hasPropertyKeyword || hasContextualPhrase || isQuestion;
      
      console.log('🔍 [CONSULTATIVE] Property question detection:', { 
        userMessage, 
        matches,
        hasPropertyKeyword,
        hasContextualPhrase,
        isQuestion,
        propertiesLoaded: this.lastSearchedProperties.length 
      });
      
      return matches;
    }
    
    return false;
  }
  
  /**
   * Extract properties mentioned by AI in its response
   */
  private extractMentionedProperties(aiMessage: string): PropertyResult[] {
    const mentionedProperties: PropertyResult[] = [];
    
    // Look for each property address in the AI's response
    this.lastSearchedProperties.forEach(property => {
      // Extract street name from full address
      const streetMatch = property.address.match(/(\d+\s+[A-Z]\s+[A-Z\s]+)/i);
      if (streetMatch) {
        const streetName = streetMatch[1];
        // Check if AI mentioned this street
        if (aiMessage.includes(streetName)) {
          mentionedProperties.push(property);
        }
      }
      
      // Also check for partial matches (e.g., "PHELPS Street")
      const words = property.address.split(' ');
      const significantWords = words.filter(w => 
        w.length > 3 && 
        !['LANE', 'STREET', 'DRIVE', 'AVENUE', 'ROAD', 'COURT'].includes(w.toUpperCase())
      );
      
      if (significantWords.some(word => aiMessage.includes(word))) {
        if (!mentionedProperties.find(p => p.id === property.id)) {
          mentionedProperties.push(property);
        }
      }
    });
    
    return mentionedProperties;
  }
  
  /**
   * Format properties for AI context
   */
  private formatPropertiesForAIContext(): string {
    // Sort by square footage to make it easy to identify largest
    const sortedProperties = [...this.lastSearchedPropertiesForAI].sort((a, b) => b.sqft - a.sqft);
    
    // Debug: Log what data we're formatting for AI
    console.log('🤖 [CONSULTATIVE] Formatting properties for AI:', {
      totalCount: sortedProperties.length,
      firstProperty: sortedProperties[0],
      propertiesWithPools: sortedProperties.filter(p => p.pool).length,
      propertiesWithYearBuilt: sortedProperties.filter(p => p.yearBuilt).length
    });
    
    return sortedProperties.map((prop, index) => {
      const features = [
        prop.pool && 'Pool',
        prop.fireplace && 'Fireplace',
        prop.garage && `${prop.garage}-car garage`,
        prop.viewType && `${prop.viewType} views`
      ].filter(Boolean).join(', ');
      
      const sizeRank = index === 0 ? ' [LARGEST]' : index === 1 ? ' [2ND LARGEST]' : index === 2 ? ' [3RD LARGEST]' : '';
      
      return `Property ${index + 1}${sizeRank}:
- Address: ${prop.address}
- Price: $${prop.price.toLocaleString()}${prop.priceReduction ? ' (PRICE REDUCED!)' : ''}
- Size: ${prop.beds} beds, ${prop.baths} baths, ${prop.sqft.toLocaleString()} sq ft
- Price/SqFt: $${prop.pricePerSqft || Math.round(prop.price / prop.sqft)}
- Year Built: ${prop.yearBuilt || 'Unknown'}
- Days on Market: ${prop.daysOnMarket || 'Unknown'}${prop.listingDate ? ` (Listed: ${new Date(prop.listingDate).toLocaleDateString()})` : ''}
- HOA Fee: ${prop.hoaFee ? `$${prop.hoaFee}/month` : 'None'}
- Neighborhood: ${prop.subdivision || 'Not specified'}
- School District: ${prop.schoolDistrict || 'Not specified'}
- Features: ${features || 'Standard'}${prop.spa ? ', Spa/Hot Tub' : ''}
- Virtual Tour: ${prop.virtualTour ? 'Available' : 'Not available'}
- Status: ${prop.status || 'Active'}
- Description excerpt: "${prop.description?.substring(0, 150) || 'No description'}..."`;
    }).join('\n\n');
  }

  /**
   * Generate lifestyle-based match reason
   */
  private generateLifestyleMatchReason(property: any): string {
    const reasons = [];
    const criteria = this.context.extractedCriteria;
    const lifestyle = criteria.lifestyle || [];
    
    // Lifestyle matches
    if (lifestyle.includes('family-oriented') && property.BedroomsTotal >= 3) {
      reasons.push('Great for growing families');
    }
    
    if (lifestyle.includes('remote-work') && property.BedroomsTotal > criteria.beds!) {
      reasons.push('Extra room for home office');
    }
    
    if (lifestyle.includes('outdoor living') && property.PoolFeatures) {
      reasons.push('Perfect for outdoor lifestyle');
    }
    
    if (lifestyle.includes('quiet neighborhood') && property.Subdivision) {
      reasons.push('Peaceful community setting');
    }
    
    // Motivation matches
    if (criteria.motivation === 'growing family' && property.LivingArea > 2000) {
      reasons.push('Spacious for your growing needs');
    }
    
    // Basic matches as fallback
    if (reasons.length === 0) {
      if (criteria.maxPrice && property.ListPrice <= criteria.maxPrice * 0.9) {
        reasons.push('Great value in your budget');
      }
      
      if (criteria.features?.includes('pool') && property.PoolFeatures) {
        reasons.push('Has the pool you wanted');
      }
    }
    
    return reasons.join(' • ') || 'Matches your search criteria';
  }

  /**
   * Get conversation insights for UI display
   */
  public getConversationInsights(): any {
    return {
      stage: this.context.conversationStage,
      hasLocation: this.context.hasLocation,
      hasBudget: this.context.hasBudget,
      hasFeatures: this.context.hasFeatures,
      hasMotivation: this.context.hasMotivation,
      hasTimeline: this.context.hasTimeline,
      extractedCriteria: this.context.extractedCriteria,
      messageCount: this.conversationHistory.length,
      conversationDepth: this.context.conversationDepth,
      isReadyForSearch: this.hasComprehensiveContext()
    };
  }

  /**
   * Get search progress by running a quick background search
   */
  private async getSearchProgress(): Promise<{ matchCount: number; searchQuality: 'initial' | 'too_broad' | 'refining' | 'optimal' | 'ready' | 'no_results' }> {
    // If we don't have basic criteria, return initial state
    if (!this.context.hasLocation && !this.context.hasBudget) {
      return { matchCount: 0, searchQuality: 'initial' };
    }

    try {
      // Make a quick search request to get count
      const response = await axiosInstance.post('/theta-ai/search', {
        query: this.buildSearchQuery(),
        criteria: this.context.extractedCriteria,
        countOnly: true, // Just get count, not full results
        sessionId: this.sessionId
      });

      const matchCount = response.data.totalFound || 0;
      
      // Determine search quality based on count and criteria completeness
      let searchQuality: 'initial' | 'too_broad' | 'refining' | 'optimal' | 'ready' | 'no_results';
      
      if (matchCount === 0) {
        searchQuality = 'no_results';
      } else if (matchCount > 50) {
        searchQuality = 'too_broad';
      } else if (matchCount > 20) {
        searchQuality = 'refining';
      } else if (matchCount > 5) {
        searchQuality = this.hasComprehensiveContext() ? 'optimal' : 'refining';
      } else {
        searchQuality = 'ready';
      }

      console.log('📊 [SEARCH PROGRESS]', { matchCount, searchQuality });
      
      return { matchCount, searchQuality };
      
    } catch (error) {
      console.error('Error getting search progress:', error);
      // Return sensible defaults on error
      if (this.context.conversationDepth < 2) {
        return { matchCount: 0, searchQuality: 'initial' };
      }
      return { matchCount: 0, searchQuality: 'refining' };
    }
  }

  /**
   * Build search query from extracted criteria
   */
  private buildSearchQuery(): string {
    const parts = [];
    const c = this.context.extractedCriteria;
    
    if (c.city) parts.push(c.city);
    if (c.beds) parts.push(`${c.beds} bedroom`);
    if (c.maxPrice) parts.push(`under $${c.maxPrice}`);
    if (c.features?.includes('pool')) parts.push('with pool');
    if (c.features?.includes('good schools')) parts.push('good schools');
    
    return parts.join(' ') || 'homes';
  }

  /**
   * Log conversation to database for future analysis and improvement
   * KOPS-343: Implement conversation logging for AI training
   */
  private async logConversation(userMessage: string, aiResponse: string): Promise<void> {
    try {
      // Send to backend for database logging
      await axiosInstance.post('/chat/public/log-conversation', {
        sessionId: this.sessionId,
        conversationId: this.sessionId,
        userMessage: userMessage,
        aiResponse: aiResponse,
        metadata: {
          stage: this.context.conversationStage,
          depth: this.context.conversationDepth,
          extractedCriteria: this.context.extractedCriteria,
          hasComprehensiveContext: this.hasComprehensiveContext(),
          source: 'chat.realestate',
          userAgent: navigator.userAgent
        }
      });
      
      console.log('💾 [CONVERSATION] Logged to database for analysis');
    } catch (error) {
      // Don't let logging failures affect user experience
      console.error('Failed to log conversation:', error);
    }
  }
}

export default ChatRealEstateConsultativeService;